import { Component, Output, EventEmitter, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  X,
  User,
  Mail,
  Shield,
  Building,
  Calendar,
  LogOut,
  Key,
  CheckCircle,
  AlertCircle,
} from 'lucide-angular';
import { AuthService } from '../../../core/services/auth.service';
import { CompaniesService } from '../../../core/services/companies.service';
import { User as UserModel } from '../../../core/models/auth.model';

@Component({
  selector: 'app-user-profile-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
         (click)="closeModal()">
      
      <!-- Card do Modal -->
      <div class="bg-white rounded-2xl max-w-4xl w-full mx-4 shadow-2xl border border-gray-200 overflow-hidden animate-slide-up max-h-[90vh] overflow-y-auto"
           (click)="$event.stopPropagation()">
        
        <!-- Header com gradiente -->
        <div class="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-gray-900 to-gray-700 sticky top-0 z-10">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 rounded-xl flex items-center justify-center bg-white/20">
              <lucide-icon [img]="UserIcon" size="28" class="text-white"></lucide-icon>
            </div>
            <div>
              <h2 class="text-lg font-bold text-white">Perfil do Usuário</h2>
              <p class="text-sm text-white/80">{{ user?.first_name }} {{ user?.last_name }}</p>
            </div>
          </div>
          <button (click)="closeModal()" 
                  class="p-2 hover:bg-white/20 rounded-lg transition-colors text-white">
            <lucide-icon [img]="XIcon" size="20"></lucide-icon>
          </button>
        </div>

        <!-- Status Bar -->
        <div class="px-6 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold uppercase tracking-wider"
                  [ngClass]="user?.is_active ? 'bg-green-50 text-green-700 border border-green-200/50' : 'bg-red-50 text-red-700 border border-red-200/50'">
              <span class="w-1.5 h-1.5 rounded-full mr-1.5"
                    [ngClass]="user?.is_active ? 'bg-green-500' : 'bg-red-500'"></span>
              {{ user?.is_active ? 'Activo' : 'Inactivo' }}
            </span>
            <span class="text-xs text-gray-400">•</span>
            <span class="text-xs text-gray-500">ID: {{ user?.id }}</span>
          </div>
          <span class="text-xs text-gray-400">
            <lucide-icon [img]="CalendarIcon" size="12" class="inline mr-1"></lucide-icon>
            Membro desde {{ formatDate(user?.created_at) }}
          </span>
        </div>

        <!-- Body -->
        <div class="p-6 space-y-4">
          <!-- Avatar e Informações Principais -->
          <div class="flex items-center gap-4 pb-4 border-b border-gray-100">
            <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-200 flex items-center justify-center text-gray-400 shadow-sm flex-shrink-0">
              <lucide-icon [img]="UserIcon" class="w-8 h-8 text-gray-500"></lucide-icon>
            </div>
            <div class="flex-1">
              <h3 class="text-xl font-semibold text-gray-900">
                {{ user?.first_name }} {{ user?.last_name }}
              </h3>
              <p class="text-sm text-gray-500">@{{ user?.username }}</p>
              <div class="flex items-center gap-2 mt-1">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                  {{ getRoleLabel(user?.role || '') }}
                </span>
              </div>
            </div>
          </div>

          <!-- Grid de Informações -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <!-- E-mail -->
            <div class="flex items-start space-x-3.5 p-3 rounded-xl bg-gray-50/50 border border-gray-100/50 hover:border-gray-200 transition-colors">
              <lucide-icon [img]="MailIcon" class="w-4 h-4 text-gray-400 mt-0.5"></lucide-icon>
              <div class="flex-1 min-w-0">
                <span class="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">E-mail</span>
                <span class="text-xs font-medium text-gray-700 break-all">{{ user?.email }}</span>
              </div>
            </div>

            <!-- Função -->
            <div class="flex items-start space-x-3.5 p-3 rounded-xl bg-gray-50/50 border border-gray-100/50 hover:border-gray-200 transition-colors">
              <lucide-icon [img]="ShieldIcon" class="w-4 h-4 text-gray-400 mt-0.5"></lucide-icon>
              <div class="flex-1 min-w-0">
                <span class="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Função</span>
                <span class="text-xs font-medium text-gray-700">{{ getRoleLabel(user?.role || '') }}</span>
              </div>
            </div>

            <!-- Empresa -->
            <div class="flex items-start space-x-3.5 p-3 rounded-xl bg-gray-50/50 border border-gray-100/50 hover:border-gray-200 transition-colors">
              <lucide-icon [img]="BuildingIcon" class="w-4 h-4 text-gray-400 mt-0.5"></lucide-icon>
              <div class="flex-1 min-w-0">
                <span class="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Empresa</span>
                <span class="text-xs font-medium text-gray-700">ID: {{ user?.company_id }}</span>
              </div>
            </div>

            <!-- Código de Acesso -->
            @if (companyAccessCode) {
              <div class="flex items-start space-x-3.5 p-3 rounded-xl bg-gray-50/50 border border-gray-100/50 hover:border-gray-200 transition-colors md:col-span-2">
                <lucide-icon [img]="KeyIcon" class="w-4 h-4 text-gray-400 mt-0.5"></lucide-icon>
                <div class="flex-1 min-w-0">
                  <span class="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Código de Acesso</span>
                  <span class="inline-block text-xs font-mono font-bold text-gray-800 bg-gray-100 px-2 py-1 rounded mt-1 select-all tracking-wider border border-gray-200">
                    {{ companyAccessCode }}
                  </span>
                </div>
              </div>
            }
          </div>

          <!-- Detalhes Adicionais -->
          <!-- @if (user?.created_at) {
            <div class="p-3 bg-blue-50 rounded-xl border border-blue-200">
              <div class="flex items-center gap-2">
                <lucide-icon [img]="CalendarIcon" size="14" class="text-blue-600"></lucide-icon>
                <span class="text-xs text-blue-700 font-medium">Membro desde</span>
                <span class="text-sm text-blue-800 font-semibold">{{ formatDate(user?.created_at) }}</span>
              </div>
            </div>
          } -->
        </div>

        <!-- Footer com Ações -->
        <div class="flex items-center justify-between p-6 bg-gray-50/80 border-t border-gray-100 sticky bottom-0 bg-white">
          <div class="text-xs text-gray-400">
            <lucide-icon [img]="CheckCircleIcon" size="12" class="inline mr-1 text-green-500"></lucide-icon>
            Sessão ativa
          </div>
          <div class="flex gap-3">
            <button (click)="closeModal()" 
                    class="px-4 py-2.5 border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors text-gray-700 font-medium text-sm">
              Fechar
            </button>
            <button (click)="onLogout()" 
                    class="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors flex items-center gap-2 font-medium text-sm shadow-lg shadow-red-200/50">
              <lucide-icon [img]="LogOutIcon" size="16"></lucide-icon>
              Sair da conta
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Estilos -->
    <style>
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { transform: translateY(20px) scale(0.98); opacity: 0; }
        to { transform: translateY(0) scale(1); opacity: 1; }
      }
      .animate-fade-in {
        animation: fadeIn 0.3s ease-out;
      }
      .animate-slide-up {
        animation: slideUp 0.3s ease-out;
      }
    </style>
  `
})
export class UserProfileModalComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  
  user: UserModel | null = null;
  companyAccessCode: string | null = null;

  // Ícones
  readonly XIcon = X;
  readonly UserIcon = User;
  readonly MailIcon = Mail;
  readonly ShieldIcon = Shield;
  readonly BuildingIcon = Building;
  readonly CalendarIcon = Calendar;
  readonly LogOutIcon = LogOut;
  readonly KeyIcon = Key;
  readonly CheckCircleIcon = CheckCircle;
  readonly AlertCircleIcon = AlertCircle;

  constructor(
    private authService: AuthService,
    private companiesService: CompaniesService
  ) {}

  ngOnInit(): void {
    this.user = this.authService.getCurrentUser();
    
    if (this.user?.company_id) {
      this.loadCompanyAccessCode();
    }
  }

  loadCompanyAccessCode(): void {
    if (!this.user?.company_id) return;
    
    this.companiesService.getById(this.user.company_id.toString()).subscribe({
      next: (response: any) => {
        const company = response.data || response;
        this.companyAccessCode = company?.access_code || null;
      },
      error: () => {
        this.companyAccessCode = null;
      }
    });
  }

  closeModal(): void {
    this.close.emit();
  }

  onLogout(): void {
    this.authService.logout();
    this.closeModal();
  }

  getRoleLabel(role: string): string {
    const roleLabels: { [key: string]: string } = {
      admin: 'Administrador',
      manager: 'Gerente',
      supervisor: 'Supervisor',
      operator: 'Operador',
      user: 'Usuário',
      guard: 'Vigilante',
    };
    return roleLabels[role] || role;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}